import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxProps, Tooltip } from '@chakra-ui/react';
import { parseJSON } from 'date-fns';

import { isPrintType } from '@/lib/utils';
import { AdListItem } from '@/pages/MyAds/types';
import { getFilterStatus } from '@/pages/MyAds/utils';
import { Tag, TagColor } from './Tag';

export type ListStatusBadgeProps = Omit<BoxProps, 'backgroundColor'> & {
  item: AdListItem;
};

export const ListStatusBadge: FC<ListStatusBadgeProps> = ({ item, ...rest }) => {
  const { t } = useTranslation();
  const { addressStatus, type: dataObjectType } = item;
  let dataObjectStatus = item.status;

  const showVerificationStatus =
    addressStatus &&
    addressStatus !== 'Verified' &&
    (dataObjectStatus === 'Active' || dataObjectStatus === 'ToApproveChanges') &&
    !isPrintType(dataObjectType);

  // pending changes read as "in review", the same as an advert waiting for its first approval
  if (showVerificationStatus || dataObjectStatus === 'ToApproveChanges') {
    dataObjectStatus = 'ToApprove';
  }

  const hasFutureStart = useMemo(
    () => (item.publicationStart ? parseJSON(item.publicationStart) > new Date() : false),
    [item.publicationStart]
  );

  // only an ad that is still going online can be planned - a stopped one keeps the start date of its prolongation
  const isPlanned = hasFutureStart && getFilterStatus(dataObjectStatus) === 'active';
  const label = isPlanned ? 'ad.status.planned' : `ad.status.${dataObjectStatus.toString().toLowerCase()}`;

  const color: TagColor = useMemo(() => {
    switch (dataObjectStatus) {
      case 'Active':
      case 'Approved':
        return 'green';
      case 'Draft':
        return 'yellow';
      case 'ToApprove':
        return 'blue';
      case 'Disabled':
        return 'gray';
      case 'Expired':
        return 'red';
      default:
        return 'transparent';
    }
  }, [dataObjectStatus]);

  return (
    <Box position="relative" {...rest}>
      <Tooltip
        label={t(`myads.addressStatus.${addressStatus?.toLowerCase()}`, '')}
        placement="top"
        fontSize="md"
        hasArrow
        hidden={!showVerificationStatus}
      >
        <Tag backgroundColor={color} label={t(label, { defaultValue: 'Unknown' }).toUpperCase()} />
      </Tooltip>
    </Box>
  );
};
